import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { Select } from "components/ui/Select/Select";

import style from "./PostnatalBabyObservationSection.module.css";

const DELIVERY_METHOD_OPTIONS = [
  { label: "C-section", value: "c-section" },
  { label: "Vaginal delivery", value: "vaginal-delivery" },
  {
    label: "Assisted vaginal delivery (vacuum or forceps)",
    value: "assisted-vaginal-delivery",
  },
];

const FEEDING_OPTIONS = [
  { label: "Breast", value: "Breast" },
  { label: "Formula", value: "Formula" },
  { label: "Combination", value: "Breast and Formula combination" },
];

const CRT_OPTIONS = [
  { label: "Normal (<2s)", value: "yes" },
  { label: "Prolonged (≥2s)", value: "no" },
];

const numericOnly = (event) =>
  (event.target.value = event.target.value.replace(/[^0-9]/g, ""));

// Per-baby postnatal assessment (KIN-1075) — the postnatal analogue of
// FoetalObservationSection. Controlled via `values`/`onChange`; reuses the
// parent form's CSS via `formStyle`.
export const PostnatalBabyObservationSection = ({
  foetusIdentifier,
  values,
  errors,
  onChange,
  formStyle,
}) => {
  const set = (field) => (value) => onChange({ ...values, [field]: value });
  const setInput = (field) => (event) =>
    onChange({ ...values, [field]: event.target.value });

  const fieldError = (field) =>
    errors?.[field] ? <p className={style.fieldError}>{errors[field]}</p> : null;

  const required = <span className={formStyle.requiredMark}>*</span>;

  return (
    <div className={style.card}>
      {foetusIdentifier && (
        <p className={style.cardTitle}>Baby {foetusIdentifier}</p>
      )}

      <h4 className={style.subTitle}>Subject</h4>
      <div className={formStyle.inputGroupGrid5Columns}>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Born at</label>
          <DatePickerField
            value={values.bornAt}
            onChange={set("bornAt")}
          />
          {fieldError("bornAt")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Delivery method</label>
          <Select
            options={DELIVERY_METHOD_OPTIONS}
            value={values.deliveryMethod}
            onChange={set("deliveryMethod")}
            placeholder={null}
            fullWidth
          />
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>
            Weight at birth {required}
          </label>
          <div className={formStyle.unitInput} data-unit="g">
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="e.g. 3200"
              onInput={numericOnly}
              value={values.weightAtBirth}
              onChange={setInput("weightAtBirth")}
            />
          </div>
          {fieldError("weightAtBirth")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Feeding</label>
          <Select
            options={FEEDING_OPTIONS}
            value={values.feeding}
            onChange={set("feeding")}
            placeholder={null}
            fullWidth
          />
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>PHN Visit {required}</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Scheduled at 2 weeks"
            value={values.phnVisit}
            onChange={setInput("phnVisit")}
          />
          {fieldError("phnVisit")}
        </div>
      </div>
      <div>
        <label className={formStyle.pregnancyFormLabel}>Issues of note {required}</label>
        <textarea
          className="form-control textareaform-control"
          style={{ minHeight: "120px" }}
          placeholder="e.g. Jaundice noted at 24h, resolved by day 3"
          value={values.issuesOfNote}
          onChange={setInput("issuesOfNote")}
        />
        {fieldError("issuesOfNote")}
        <label
          className={`${formStyle.pregnancyFormCheckboxLabel} ${formStyle.pregnancyFormCheckboxLabelWithTopMargin}`}
        >
          <CustomCheckbox
            checked={!!values.jaundice}
            onChange={(event) =>
              onChange({ ...values, jaundice: event.target.checked })
            }
          />
          Jaundice
        </label>
      </div>

      <h4 className={style.subTitle}>Objective</h4>
      <div className={formStyle.inputGroupGrid5Columns}>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Weight {required}</label>
          <div className={formStyle.unitInput} data-unit="g">
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="e.g. 3500"
              onInput={numericOnly}
              value={values.weight}
              onChange={setInput("weight")}
            />
          </div>
          {fieldError("weight")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Length {required}</label>
          <div className={formStyle.unitInput} data-unit="cm">
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="e.g. 50"
              onInput={numericOnly}
              value={values.length}
              onChange={setInput("length")}
            />
          </div>
          {fieldError("length")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>
            Head circumference {required}
          </label>
          <div className={formStyle.unitInput} data-unit="cm">
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="e.g. 34"
              onInput={numericOnly}
              value={values.headCircumference}
              onChange={setInput("headCircumference")}
            />
          </div>
          {fieldError("headCircumference")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>
            CRT (Capillary Refill Time)
          </label>
          <Select
            options={CRT_OPTIONS}
            value={values.crt}
            onChange={set("crt")}
            placeholder={null}
            fullWidth
          />
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Umbilicus {required}</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Healing well, no infection"
            value={values.umbilicus}
            onChange={setInput("umbilicus")}
          />
          {fieldError("umbilicus")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>
            Barlow and Ortolani's Test {required}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Negative bilaterally"
            value={values.barlowAndOrtolaniTest}
            onChange={setInput("barlowAndOrtolaniTest")}
          />
          {fieldError("barlowAndOrtolaniTest")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Heart {required}</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Normal heart sounds, no murmur"
            value={values.heart}
            onChange={setInput("heart")}
          />
          {fieldError("heart")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Fontanelles {required}</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Flat and soft"
            value={values.fontanelles}
            onChange={setInput("fontanelles")}
          />
          {fieldError("fontanelles")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Buccal Mucosa {required}</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Moist and pink, no thrush"
            value={values.buccalMucosa}
            onChange={setInput("buccalMucosa")}
          />
          {fieldError("buccalMucosa")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Feet {required}</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Normal, no talipes"
            value={values.feet}
            onChange={setInput("feet")}
          />
          {fieldError("feet")}
        </div>
      </div>
      <div className={formStyle.inputGroupGrid2Columns}>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Impression {required}</label>
          <textarea
            className="form-control textareaform-control"
            style={{ minHeight: "120px" }}
            placeholder="e.g. Healthy newborn, feeding well, appropriate weight gain"
            value={values.impression}
            onChange={setInput("impression")}
          />
          {fieldError("impression")}
        </div>
        <div>
          <label className={formStyle.pregnancyFormLabel}>Plan {required}</label>
          <textarea
            className="form-control textareaform-control"
            style={{ minHeight: "120px" }}
            placeholder="e.g. Review in 6 weeks, monitor weight gain, routine immunisations"
            value={values.plan}
            onChange={setInput("plan")}
          />
          {fieldError("plan")}
        </div>
      </div>
    </div>
  );
};
